// RhymeCategoryTooltip - Educational content section for RhymePopover
// Shows category examples and stability explanation
// Lightweight: uses preloaded data, no reflows, <5ms render target

import { memo, useMemo } from 'react'
import type { RhymeCategory } from '../../../utils/rhyme/types'
import { RHYME_CATEGORY_EXPLANATIONS, type RhymeCategoryDescription } from '../../../content/rhyme-category-explanations'
import '../components/rhymeBadge.css'

interface RhymeCategoryTooltipProps {
  /** The rhyme category to explain */
  category: RhymeCategory
  /** Whether this rhyme matches the stability expectation for its position */
  isExpected: boolean
}

/**
 * RhymeCategoryTooltip - Educational content for rhyme categories
 *
 * Renders within RhymePopover to provide:
 * - Example word pairs for the category
 * - Stability context (matches/adds tension)
 *
 * Performance: Data is preloaded via RHYME_CATEGORY_EXPLANATIONS map.
 * No DOM queries, no async operations, no reflows.
 *
 * Accessibility: Uses definition list semantics, aria-label for examples,
 * and inherits dialog context from parent RhymePopover.
 */
const RhymeCategoryTooltip = memo(function RhymeCategoryTooltip({
  category,
  isExpected
}: RhymeCategoryTooltipProps) {
  // Precompute all display data in a single useMemo to avoid per-field lookups
  const displayData = useMemo(() => {
    const desc: RhymeCategoryDescription = RHYME_CATEGORY_EXPLANATIONS[category]
    return {
      examples: desc.examples,
      stabilityText: isExpected
        ? desc.stabilityNote.matchesExpectation
        : desc.stabilityNote.addsTension
    }
  }, [category, isExpected])

  return (
    <div
      className="rhyme-category-tooltip"
      role="region"
      aria-label="Rhyme category education"
      data-testid="rhyme-category-tooltip"
    >
      {/* Examples section */}
      <div className="rhyme-category-tooltip__examples">
        <div className="rhyme-category-tooltip__section-header">
          Examples
        </div>
        <dl className="rhyme-category-tooltip__example-list" aria-label="Example rhyme pairs">
          {displayData.examples.map((example, index) => (
            <div key={index} className="rhyme-category-tooltip__example-pair">
              <dt className="rhyme-category-tooltip__word">{example.word1}</dt>
              <dd className="rhyme-category-tooltip__word">{example.word2}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Stability context */}
      <div
        className="rhyme-category-tooltip__stability"
        aria-live="polite"
        data-testid="rhyme-category-tooltip-stability"
      >
        <div className="rhyme-category-tooltip__section-header">
          {isExpected ? '✓ Matches Expectation' : '◇ Adds Tension'}
        </div>
        <p className="rhyme-category-tooltip__stability-text">
          {displayData.stabilityText}
        </p>
      </div>
    </div>
  )
})

export default RhymeCategoryTooltip
export type { RhymeCategoryTooltipProps }
